import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { sendOTPEmail } from '../services/emailService';
import {
  customerApi,
  setCustomerSession,
  getStoredCustomer,
  getCustomerToken,
  clearCustomerSession,
} from '../services/customerApi';
import { clearLocalCustomerSeeds } from '../services/userService';

const CustomerAuthContext = createContext(null);

function mapCustomer(c) {
  if (!c || typeof c !== 'object') return null;
  const status = String(c.status || 'active');
  return {
    id: c.id,
    name: c.name || 'Customer',
    email: String(c.email || '').trim().toLowerCase(),
    phone: c.phone || '',
    address: c.address || '',
    city: c.city || '',
    country: c.country || 'Pakistan',
    status,
    banned: status === 'banned',
    newsletter_optin: Boolean(c.newsletter_optin),
    whatsapp_updates: Boolean(c.whatsapp_updates),
    source: c.source || 'register',
    created_at: c.created_at,
  };
}

function apiError(err, fallback) {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  if (data?.errors) {
    const first = Object.values(data.errors).flat()[0];
    if (first) return String(first);
  }
  return err?.message || fallback;
}

export function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clearLocalCustomerSeeds();
    (async () => {
      const token = getCustomerToken();
      const stored = mapCustomer(getStoredCustomer());
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      if (stored) setUser(stored);
      try {
        const { data } = await customerApi.me();
        const mapped = mapCustomer(data.customer);
        if (!mapped || mapped.banned) {
          clearCustomerSession();
          setUser(null);
        } else {
          setCustomerSession(token, mapped);
          setUser(mapped);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sendRegisterOTP = useCallback(async (email, name = '') => {
    const e = String(email || '').trim().toLowerCase();
    if (!e) throw new Error('Email is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) throw new Error('Please enter a valid email address.');
    return sendOTPEmail({ email: e, name, type: 'VERIFY' });
  }, []);

  const registerWithOTP = useCallback(async (payload, otp) => {
    if (!payload.password) throw new Error('Password is required');
    if (String(payload.password).length < 6) throw new Error('Password must be at least 6 characters');
    const email = String(payload.email || '').trim().toLowerCase();
    if (!email) throw new Error('Email is required');
    if (!payload.name) payload.name = email.split('@')[0];
    try {
      const { data } = await customerApi.register({
        name: payload.name,
        email,
        otp: String(otp || '').replace(/\D/g, ''),
        phone: payload.phone || '',
        password: payload.password,
        address: payload.address || '',
        city: payload.city || '',
        newsletter_optin: payload.newsletter_optin !== undefined ? Boolean(payload.newsletter_optin) : true,
        whatsapp_updates: payload.whatsapp_updates !== undefined ? Boolean(payload.whatsapp_updates) : true,
        source: payload.source || 'register',
      });
      const mapped = mapCustomer(data.customer);
      setCustomerSession(data.token, mapped);
      setUser(mapped);
      return mapped;
    } catch (err) {
      throw new Error(apiError(err, 'Could not create account.'));
    }
  }, []);

  const sendForgotOTP = useCallback(async (email) => {
    const e = String(email || '').trim().toLowerCase();
    if (!e) throw new Error('Email is required');
    return sendOTPEmail({ email: e, name: '', type: 'FORGOT' });
  }, []);

  const resetPasswordWithOTP = useCallback(async (email, otp, newPassword) => {
    const e = String(email || '').trim().toLowerCase();
    if (!e) throw new Error('Email is required');
    if (String(newPassword || '').length < 6) throw new Error('New password must be at least 6 characters');
    try {
      await customerApi.resetPassword({
        email: e,
        otp: String(otp || '').replace(/\D/g, ''),
        password: newPassword,
      });
    } catch (err) {
      throw new Error(apiError(err, 'Could not reset password.'));
    }
    return true;
  }, []);

  const login = useCallback(async (email, password) => {
    const e = String(email || '').trim().toLowerCase();
    if (!e) throw new Error('Email is required');
    if (!password) throw new Error('Password is required');
    try {
      const { data } = await customerApi.login({ email: e, password });
      const mapped = mapCustomer(data.customer);
      if (mapped?.banned) {
        clearCustomerSession();
        throw new Error('Your account has been restricted. Please contact support.');
      }
      setCustomerSession(data.token, mapped);
      setUser(mapped);
      return mapped;
    } catch (err) {
      throw new Error(apiError(err, 'Incorrect email or password.'));
    }
  }, []);

  const logout = useCallback(async () => {
    clearCustomerSession();
    setUser(null);
    return true;
  }, []);

  const updateProfile = useCallback(async (payload) => {
    if (!user) throw new Error('Not logged in');
    const body = {};
    if (payload.name !== undefined) body.name = payload.name;
    if (payload.phone !== undefined) body.phone = payload.phone;
    if (payload.address !== undefined || payload.address_line1 !== undefined) {
      body.address = payload.address ?? payload.address_line1;
    }
    if (payload.city !== undefined) body.city = payload.city;
    if (payload.newsletter_optin !== undefined) body.newsletter_optin = Boolean(payload.newsletter_optin);
    if (payload.whatsapp_updates !== undefined) body.whatsapp_updates = Boolean(payload.whatsapp_updates);
    const { data } = await customerApi.updateMe(body);
    const mapped = mapCustomer(data.customer);
    setCustomerSession(getCustomerToken(), mapped);
    setUser(mapped);
    return mapped;
  }, [user]);

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    if (!user) throw new Error('Not logged in');
    if (String(newPassword).length < 6) throw new Error('New password must be at least 6 characters');
    try {
      await customerApi.changePassword({
        current_password: oldPassword,
        password: newPassword,
      });
    } catch (err) {
      throw new Error(apiError(err, 'Could not change password.'));
    }
    toast.success('Password changed successfully.');
    return true;
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await customerApi.me();
      const mapped = mapCustomer(data.customer);
      if (!mapped || mapped.banned) return;
      setCustomerSession(getCustomerToken(), mapped);
      setUser(mapped);
    } catch {}
  }, []);

  return (
    <CustomerAuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      sendRegisterOTP,
      registerWithOTP,
      sendForgotOTP,
      resetPasswordWithOTP,
      login,
      logout,
      updateProfile,
      changePassword,
      refresh,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  return ctx;
}

export default CustomerAuthContext;
